// Tenant-level on/off gate (GrowthBook), separate from RBAC role gating above.
// Default false (opt-in): a tenant only gets the feature once its orgId is
// added to the matching GrowthBook targeting rule.
export const TENANT_FEATURE_FLAGS = Object.freeze({
  contact_tags: 'tags-enabled',
  custom_status: 'custom-status-enabled',
  orders: 'orders-enabled',
  bookings: 'bookings-enabled',
  faq: 'faq_lane_on',
  bot_logs: 'nhat_ky_bot_log',
  playbook: 'playbook_enabled',
  quick_answer: 'quick_answer_on',
})

export const RBAC_MODULES = Object.freeze({
  dashboard: 'dashboard',
  conversations: 'conversations',
  automation: 'automation',
  contacts: 'contacts',
  contactTags: 'contact_tags',
  customStatus: 'custom_status',
  bookings: 'bookings',
  orders: 'orders',
  products: 'products',
  settings: 'settings',
  botConfig: 'bot_config',
  billing: 'billing',
  adminPermissions: 'admin_permissions',
})

const PERMISSION_LEVELS = Object.freeze({
  full: 'full',
  view: 'view',
  noAccess: 'no_access',
})

const ROLE_PERMISSIONS = Object.freeze({
  receptionist: Object.freeze({
    dashboard: PERMISSION_LEVELS.view,
    conversations: PERMISSION_LEVELS.full,
    automation: PERMISSION_LEVELS.noAccess,
    contacts: PERMISSION_LEVELS.full,
    contact_tags: PERMISSION_LEVELS.noAccess,
    custom_status: PERMISSION_LEVELS.noAccess,
    bookings: PERMISSION_LEVELS.full,
    orders: PERMISSION_LEVELS.full,
    products: PERMISSION_LEVELS.view,
    settings: PERMISSION_LEVELS.full,
    bot_config: PERMISSION_LEVELS.noAccess,
    billing: PERMISSION_LEVELS.noAccess,
    admin_permissions: PERMISSION_LEVELS.noAccess,
  }),
  supervisor: Object.freeze({
    dashboard: PERMISSION_LEVELS.view,
    conversations: PERMISSION_LEVELS.full,
    automation: PERMISSION_LEVELS.noAccess,
    contacts: PERMISSION_LEVELS.full,
    contact_tags: PERMISSION_LEVELS.noAccess,
    custom_status: PERMISSION_LEVELS.noAccess,
    bookings: PERMISSION_LEVELS.full,
    orders: PERMISSION_LEVELS.full,
    products: PERMISSION_LEVELS.full,
    settings: PERMISSION_LEVELS.full,
    bot_config: PERMISSION_LEVELS.noAccess,
    billing: PERMISSION_LEVELS.noAccess,
    admin_permissions: PERMISSION_LEVELS.noAccess,
  }),
  manager: Object.freeze({
    dashboard: PERMISSION_LEVELS.full,
    conversations: PERMISSION_LEVELS.full,
    automation: PERMISSION_LEVELS.full,
    contacts: PERMISSION_LEVELS.full,
    contact_tags: PERMISSION_LEVELS.full,
    custom_status: PERMISSION_LEVELS.full,
    bookings: PERMISSION_LEVELS.full,
    orders: PERMISSION_LEVELS.full,
    products: PERMISSION_LEVELS.full,
    settings: PERMISSION_LEVELS.full,
    bot_config: PERMISSION_LEVELS.full,
    billing: PERMISSION_LEVELS.noAccess,
    admin_permissions: PERMISSION_LEVELS.full,
  }),
  admin: Object.freeze({
    dashboard: PERMISSION_LEVELS.full,
    conversations: PERMISSION_LEVELS.full,
    automation: PERMISSION_LEVELS.full,
    contacts: PERMISSION_LEVELS.full,
    contact_tags: PERMISSION_LEVELS.full,
    custom_status: PERMISSION_LEVELS.full,
    bookings: PERMISSION_LEVELS.full,
    orders: PERMISSION_LEVELS.full,
    products: PERMISSION_LEVELS.full,
    settings: PERMISSION_LEVELS.full,
    bot_config: PERMISSION_LEVELS.full,
    billing: PERMISSION_LEVELS.full,
    admin_permissions: PERMISSION_LEVELS.full,
  }),
})

const VALID_LEVELS = new Set(Object.values(PERMISSION_LEVELS))
const VALID_ROLES = new Set(Object.keys(ROLE_PERMISSIONS))

export const FULL_ACCESS = PERMISSION_LEVELS.full
export const VIEW_ACCESS = PERMISSION_LEVELS.view
export const NO_ACCESS = PERMISSION_LEVELS.noAccess

export function normalizeRole(role) {
  const normalized = String(role || '').toLowerCase().trim()
  if (normalized === 'owner') return 'admin'
  if (VALID_ROLES.has(normalized)) return normalized
  return 'admin'
}

export function getPermission(role, moduleName) {
  const normalizedRole = normalizeRole(role)
  const moduleKey = String(moduleName || '').trim()
  const permission = ROLE_PERMISSIONS[normalizedRole]?.[moduleKey]
  if (!VALID_LEVELS.has(permission)) return NO_ACCESS
  return permission
}

export function canAccess(role, moduleName, minimumLevel = VIEW_ACCESS) {
  const permission = getPermission(role, moduleName)
  if (minimumLevel === FULL_ACCESS) return permission === FULL_ACCESS
  return permission !== NO_ACCESS
}

export function canManageOrgTags(role) {
  const normalized = String(role || '').toLowerCase().trim()
  const effectiveRole = normalized === 'owner' ? 'admin' : normalized
  return effectiveRole === 'admin' || effectiveRole === 'manager'
}
